import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Radio,
  Checkbox,
  Select,
  Card,
  Space,
  Divider,
  Typography,
  Alert,
  message,
  Spin,
  Steps,
  Descriptions,
  Tag,
  Row,
  Col,
  Tooltip,
  Modal,
  Upload,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined, ArrowLeftOutlined, ArrowRightOutlined, DeleteOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import {
  Product,
  SKU,
  Entitlement,
  OrgEntitlement,
  Region,
  ProductOnboardingForm,
} from '../../types';
import './ProductOnboarding.css';
import { getTableHeaderStyle, getTableRowStyle, getSecondaryTextStyle, getDividerStyle } from './tableStyles';
import { formatEntitlementLabel, noLeadingTrailingSpaces, sanitizeTextInput } from '../../utils/utils';
import { PRODUCT_FLAG_TOOLTIPS } from './constants';
import SkuMigration from '../SkuMigration';
import { notify } from '../../utils/notify';
import { parseEntitlementCSV, type EntitlementImportMode, type ImportedEntitlement } from './entitlementImport';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const entitlementTagRender = (entitlementsList: Array<{ id?: number; name: string; value: string; description?: string; needs_quantity: boolean; unit?: string }>) =>
  (props: { label: React.ReactNode; value: unknown; closable: boolean; onClose: () => void }) => {
    const { value, closable, onClose } = props;
    const ent = entitlementsList.find((e) => e.id === value);
    const labelStr = ent ? formatEntitlementLabel(ent) : String(value ?? '');
    return (
      <Tooltip title={labelStr} placement="top">
        <Tag
          closable={closable}
          onClose={onClose}
          style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', margin: '2px 4px 2px 0' }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{labelStr}</span>
        </Tag>
      </Tooltip>
    );
  };

const ProductOnboarding: React.FC = () => {
  const [form] = Form.useForm<ProductOnboardingForm>();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<ProductOnboardingForm | null>(null);
  // State for dropdowns
  const [products, setProducts] = useState<Product[]>([]);
  const [skus, setSKUs] = useState<SKU[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [orgEntitlements, setOrgEntitlements] = useState<OrgEntitlement[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);

  // Form state
  const [onboardingType, setOnboardingType] = useState<'product' | 'sku' | 'both' | 'migrate'>('both');
  const [isNewProduct, setIsNewProduct] = useState(true);
  const [isNewSKU, setIsNewSKU] = useState(true);
  const [isOrgEntitled, setIsOrgEntitled] = useState(false);
  const [skuTypes, setSKUTypes] = useState<{ [key: number]: 'single' | 'suite' | 'addon' }>({ 0: 'single' });
  const [useExistingEntitlements, setUseExistingEntitlements] = useState(false);
  const [hasProvisioningQuestions, setHasProvisioningQuestions] = useState(false);
  const [entitlementImportOpen, setEntitlementImportOpen] = useState(false);
  const [entitlementImportText, setEntitlementImportText] = useState('');
  const [entitlementImportMode, setEntitlementImportMode] = useState<EntitlementImportMode>('standard');
  const [entitlementImportErrors, setEntitlementImportErrors] = useState<string[]>([]);

  const openEntitlementImport = () => {
    setEntitlementImportMode(isOrgEntitled ? 'org' : 'standard');
    setEntitlementImportText('');
    setEntitlementImportErrors([]);
    setEntitlementImportOpen(true);
  };

  const getEntitlementImportOptions = () => {
    const currentIsNewProduct = form.getFieldValue('isNewProduct');
    const defaultProductId = currentIsNewProduct
      ? form.getFieldValue('newProductId')
      : form.getFieldValue('existingProductId');
    const defaultProductName = currentIsNewProduct
      ? form.getFieldValue('productName')
      : products.find((product) => product.id === defaultProductId)?.name;
    return { products, defaultProductId, defaultProductName };
  };

  const getEntitlementImportResult = (append: boolean) => {
    const importOptions = getEntitlementImportOptions();
    const result = parseEntitlementCSV(entitlementImportText, entitlementImportMode, importOptions);
    if (result.errors.length > 0) {return result;}

    if (append) {
      const listName = entitlementImportMode === 'org' ? 'orgEntitlements' : 'entitlements';
      const existingRows = (form.getFieldValue(listName) || []) as ImportedEntitlement[];
      const retainedRows = existingRows.filter((row) => row.name?.trim() && row.value?.trim());
      const productScope = (row: ImportedEntitlement) => String(row.productId ?? importOptions.defaultProductId ?? 'current-product');
      const duplicateKey = (row: ImportedEntitlement) => `${productScope(row)}\u0000${row.name.trim().toLocaleLowerCase()}\u0000${row.value.trim().toLocaleLowerCase()}`;
      const seen = new Set(retainedRows.map(duplicateKey));
      const duplicate = result.rows.find((row) => seen.has(duplicateKey(row)));
      if (duplicate) {
        return { rows: [], errors: [`${duplicate.name} / ${duplicate.value} already exists in the current entitlement list. Use Replace or remove the duplicate before appending.`] };
      }
      return { ...result, rows: [...retainedRows, ...result.rows] };
    }

    return result;
  };

  const applyEntitlementImport = (append: boolean) => {
    const result = getEntitlementImportResult(append);
    setEntitlementImportErrors(result.errors);
    if (result.errors.length > 0) {return;}

    const fieldName = entitlementImportMode === 'org' ? 'orgEntitlements' : 'entitlements';
    const maxEntitlementId = Math.max(...entitlements.map((entitlement) => entitlement.id || 0), 0);
    const formUpdates: Partial<ProductOnboardingForm> = {
      [fieldName]: result.rows,
      newEntitlementIds: result.rows.map((_, index) => maxEntitlementId + index + 1),
    };
    if (entitlementImportMode === 'org') {
      const maxOrgEntitlementId = Math.max(...orgEntitlements.map((entitlement) => entitlement.id || 0), 0);
      formUpdates.newOrgEntitlementIds = result.rows.map((_, index) => maxOrgEntitlementId + index + 1);
    }
    form.setFieldsValue(formUpdates);
    setEntitlementImportOpen(false);
    setEntitlementImportText('');
    const importedMessage = `${result.rows.length} ${entitlementImportMode === 'org' ? 'org ' : ''}entitlement${result.rows.length === 1 ? '' : 's'} ${append ? 'added' : 'imported'}.`;
    if (result.warnings?.length) {
      message.warning(`${importedMessage} ${result.warnings.join(' ')}`);
    } else {
      message.success(importedMessage);
    }
  };

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate next product ID when isNewProduct is true
  useEffect(() => {
    if (isNewProduct && products.length > 0) {
      const maxId = Math.max(...products.map(p => p.id || 0), 0);
      const nextProductId = maxId + 1;
      form.setFieldsValue({ newProductId: nextProductId });
    }
  }, [isNewProduct, products, form]);

  // Calculate entitlement IDs when new entitlements are being created
  useEffect(() => {
    const formValues = form.getFieldsValue();

    if (!useExistingEntitlements && !isOrgEntitled && entitlements.length > 0) {
      // Standard entitlements only - calculate entitlement IDs
      const maxId = Math.max(...entitlements.map(e => e.id || 0), 0);
      const entitlementsArray = formValues.entitlements || [];
      const newIds = entitlementsArray.map((_: unknown, index: number) => maxId + index + 1);
      form.setFieldsValue({ newEntitlementIds: newIds });
    }

    if (!useExistingEntitlements && isOrgEntitled && entitlements.length > 0 && orgEntitlements.length > 0) {
      // Org entitled products - calculate IDs for BOTH entitlements and org_entitlements tables
      const orgEntitlementsArray = formValues.orgEntitlements || [];

      // Calculate entitlement IDs (will be inserted into entitlements table)
      const maxEntitlementId = Math.max(...entitlements.map(e => e.id || 0), 0);
      const newEntitlementIds = orgEntitlementsArray.map((_: unknown, index: number) => maxEntitlementId + index + 1);

      // Calculate org entitlement IDs (will be inserted into org_entitlements table)
      const maxOrgEntitlementId = Math.max(...orgEntitlements.map(e => e.id || 0), 0);
      const newOrgEntitlementIds = orgEntitlementsArray.map((_: unknown, index: number) => maxOrgEntitlementId + index + 1);

      form.setFieldsValue({
        newEntitlementIds: newEntitlementIds,
        newOrgEntitlementIds: newOrgEntitlementIds
      });
    }
  }, [useExistingEntitlements, isOrgEntitled, entitlements, orgEntitlements, form]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [productsData, skusData, regionsData, entitlementsData, orgEntitlementsData, provisioningQuestionsData] =
        await Promise.all([
          apiService.getProducts(),
          apiService.getSKUs(),
          apiService.getRegions(),
          apiService.getEntitlements(),
          apiService.getOrgEntitlements(),
          apiService.getAllProvisioningQuestions(),
        ]);

      setProducts(productsData);
      setSKUs(skusData);
      setRegions(regionsData);
      setEntitlements(entitlementsData);
      setOrgEntitlements(orgEntitlementsData);

      // Set lastIndices including the real max provisioning question ID
      const lastIndices = {
        products: productsData.length > 0 ? Math.max(...productsData.map(p => p.id || 0), 0) : 0,
        entitlements: entitlementsData.length > 0 ? Math.max(...entitlementsData.map(e => e.id || 0), 0) : 0,
        orgEntitlements: orgEntitlementsData.length > 0 ? Math.max(...orgEntitlementsData.map(e => e.id || 0), 0) : 0,
        provisioningQuestions: provisioningQuestionsData.length > 0 ? Math.max(...provisioningQuestionsData.map(q => q.id || 0), 0) : 0,
      };
      form.setFieldsValue({ lastIndices });
    } catch {
      message.error('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    try {
      // Validate current step fields
      const values = await form.validateFields();

      // Save current form values
      setFormData({ ...formData, ...values } as ProductOnboardingForm);

      // Move to next step
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } catch (error) {
      message.error('Please fill in all required fields');
    }
  };

  const handlePrevious = () => {
    // Save current form values before going back
    const values = form.getFieldsValue();
    setFormData({ ...formData, ...values } as ProductOnboardingForm);
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  const transformToBulkInsertPayload = (data: ProductOnboardingForm, type: 'product' | 'sku' | 'both') => {
    const bulkEntry: Array<Record<string, unknown>> = [];

    // === PRODUCT ONBOARDING ===
    if (type === 'product' || type === 'both') {
      // 1. Products table entry
      if (data.isNewProduct) {
        bulkEntry.push({
          table_key: 'products',
          entries: [
            {
              data: {
                ID: data.newProductId,
                Name: sanitizeTextInput(data.productName),
                ProvisioningClient: data.provisioningClient,
                ProvisioningStyle: data.provisioningStyle,
                Key: sanitizeTextInput(data.productKey || ''),
                IsHidden: data.is_hidden || false,
                IsAutoProvision: data.is_auto_provision || false,
                IsComposite: data.is_composite || false,
                IsBrownfieldOnly: data.is_brownfield_only || false,
                IsUnifiedOnboardingSupported: data.is_unified_onboarding_supported || false,
                IsParentProvisioningSupported: data.is_parent_provisioning_supported || false,
                InheritParentMetadata: data.inherit_parent_metadata || false,
              },
            },
          ],
        });
      }

      // 2. Entitlements or Org Entitlements
      if (!data.useExistingEntitlements) {
        const productId = data.isNewProduct ? data.newProductId : data.existingProductId;

        if (!data.isOrgEntitled && data.entitlements && data.entitlements.length > 0) {
          // Standard entitlements - calculate IDs if not present
          const maxEntitlementId = Math.max(...entitlements.map(e => e.id || 0), 0);
          const entitlementEntries = data.entitlements.map((ent, idx) => ({
            data: {
              ID: data.newEntitlementIds?.[idx] || (maxEntitlementId + idx + 1),
              ProductID: ent.productId ?? productId,
              Name: sanitizeTextInput(ent.name),
              Value: sanitizeTextInput(ent.value),
              NeedsQuantity: ent.needs_quantity || false,
              Unit: sanitizeTextInput(ent.unit || ''),
              Description: sanitizeTextInput(ent.description || ''),
            },
          }));
          bulkEntry.push({
            table_key: 'entitlements',
            entries: entitlementEntries,
          });
        } else if (data.isOrgEntitled && data.orgEntitlements && data.orgEntitlements.length > 0) {
          // Org entitlements - calculate IDs if not present
          const maxEntitlementId = Math.max(...entitlements.map(e => e.id || 0), 0);
          const maxOrgEntitlementId = Math.max(...orgEntitlements.map(e => e.id || 0), 0);

          // Insert into both entitlements and org_entitlements
          const entitlementEntries = data.orgEntitlements.map((ent, idx) => ({
            data: {
              ID: data.newEntitlementIds?.[idx] || (maxEntitlementId + idx + 1),
              ProductID: ent.productId ?? productId,
              Name: sanitizeTextInput(ent.name),
              Value: sanitizeTextInput(ent.value),
              NeedsQuantity: ent.needs_quantity || false,
              Unit: sanitizeTextInput(ent.unit || ''),
              Description: sanitizeTextInput(ent.description || ''),
            },
          }));
          bulkEntry.push({
            table_key: 'entitlements',
            entries: entitlementEntries,
          });

          const orgEntitlementEntries = data.orgEntitlements.map((ent, idx) => ({
            data: {
              ID: data.newOrgEntitlementIds?.[idx] || (maxOrgEntitlementId + idx + 1),
              ProductID: ent.productId ?? productId,
              Name: sanitizeTextInput(ent.name),
              Value: sanitizeTextInput(ent.value),
              NeedsQuantity: ent.needs_quantity || false,
              Unit: sanitizeTextInput(ent.unit || ''),
              Description: sanitizeTextInput(ent.description || ''),
              AccessType: ent.access_type,
              IsService: ent.is_service || false,
            },
          }));
          bulkEntry.push({
            table_key: 'org_entitlements',
            entries: orgEntitlementEntries,
          });
        }
      }

      // 3. Product allowed regions
      if (data.allowedRegions && data.allowedRegions.length > 0 && data.isNewProduct) {
        const regionEntries = data.allowedRegions.map((regionName) => ({
          data: {
            ProductID: data.newProductId,
            RegionName: regionName,
          },
        }));
        bulkEntry.push({
          table_key: 'product_allowed_regions',
          entries: regionEntries,
        });
      }

      // 4. Provisioning questions (optional)
      if (data.hasProvisioningQuestions && data.provisioningQuestions && data.provisioningQuestions.length > 0 && data.isNewProduct) {
        const questionEntries = data.provisioningQuestions.map((q, idx) => ({
          data: {
            ID: (data.lastIndices?.provisioningQuestions || 0) + idx + 1,
            Name: q.name,
            DisplayName: q.display_name,
            Description: q.description || '',
            Type: q.type,
            ProductID: data.newProductId,
          },
        }));
        bulkEntry.push({
          table_key: 'provisioning_questions',
          entries: questionEntries,
        });
      }
    }

    // === SKU ONBOARDING ===
    if (type === 'sku' || type === 'both') {
      // Handle multiple SKUs
      if (data.isNewSKU && data.skus && data.skus.length > 0) {
        const maxSkuId = Math.max(...skus.map(s => s.id || 0), 0);

        // Pre-calculate how many SKU rows will be inserted in total
        // (each "both" model SKU contributes 2 rows)
        let skuIdCursor = maxSkuId;

        // Process each SKU
        data.skus.forEach((sku) => {
          const isBoth = sku.buyingModel === 'both';

          // Assign IDs
          skuIdCursor += 1;
          const alcSkuId = skuIdCursor;
          let eaSkuId: number | null = null;
          if (isBoth) {
            skuIdCursor += 1;
            eaSkuId = skuIdCursor;
          }
          const currentSkuId = alcSkuId; // primary ID used for all config rows (ALC)

          // Helper: build config entries for a given skuId
          const buildSkuConfigEntries = (targetSkuId: number) => {
            if (sku.skuType !== 'addon' && sku.productConfigs && sku.productConfigs.length > 0) {
              const productSkuConfigEntries = sku.productConfigs.map((config) => {
                const configData: Record<string, unknown> = {
                  ProductID: config.productId,
                  SkuID: targetSkuId,
                };
                if (config.operation) {configData.Operation = config.operation;}
                if (config.operand) {configData.Operand = config.operand;}
                if (config.forceRegionName) {configData.ForceRegionName = sanitizeTextInput(config.forceRegionName);}
                return { data: configData };
              });
              bulkEntry.push({ table_key: 'product_sku_configs', entries: productSkuConfigEntries });

              const entitlementMappings: Array<Record<string, unknown>> = [];
              sku.productConfigs.forEach((config) => {
                if (config.entitlementIds && config.entitlementIds.length > 0) {
                  config.entitlementIds.forEach((entitlementId) => {
                    entitlementMappings.push({
                      data: {
                        ProductSkuConfigProductID: config.productId,
                        ProductSkuConfigSkuID: targetSkuId,
                        EntitlementID: entitlementId,
                      },
                    });
                  });
                }
              });
              if (entitlementMappings.length > 0) {
                bulkEntry.push({ table_key: 'product_sku_config_entitlements', entries: entitlementMappings });
              }
            }
          };

          const buildAddonEntries = (targetSkuId: number) => {
            if (sku.skuType === 'addon' && sku.productConfigs && sku.productConfigs.length > 0) {
              const addonConfig = sku.productConfigs[0];
              const baseProductId = addonConfig.productId;
              if (baseProductId) {
                const configData: Record<string, unknown> = { ProductID: baseProductId, SkuID: targetSkuId };
                if (addonConfig.operation) {configData.Operation = addonConfig.operation;}
                if (addonConfig.operand) {configData.Operand = addonConfig.operand;}
                if (addonConfig.forceRegionName) {configData.ForceRegionName = sanitizeTextInput(addonConfig.forceRegionName);}
                bulkEntry.push({ table_key: 'product_sku_configs', entries: [{ data: configData }] });

                if (addonConfig.entitlementIds && addonConfig.entitlementIds.length > 0) {
                  bulkEntry.push({
                    table_key: 'product_sku_config_entitlements',
                    entries: addonConfig.entitlementIds.map((entitlementId: number) => ({
                      data: {
                        ProductSkuConfigProductID: baseProductId,
                        ProductSkuConfigSkuID: targetSkuId,
                        EntitlementID: entitlementId,
                      },
                    })),
                  });
                }

                const addonConfigData: Record<string, unknown> = { SkuID: targetSkuId, BaseProductID: baseProductId };
                if (sku.addonDescription) {addonConfigData.SkuDescription = sanitizeTextInput(sku.addonDescription);}
                if (sku.addonType) {addonConfigData.AddOnType = sku.addonType;}
                if (addonConfig.allowedRegions && addonConfig.allowedRegions.length > 0) {
                  addonConfigData.AllowedRegions = addonConfig.allowedRegions.join(',');
                }
                bulkEntry.push({ table_key: 'product_addon_config', entries: [{ data: addonConfigData }] });
              }
            }
          };

          // 1. ALC SKU row
          bulkEntry.push({
            table_key: 'skus',
            entries: [
              {
                data: {
                  ID: alcSkuId,
                  Name: sanitizeTextInput(sku.skuName || ''),
                  IsExternal: sku.isExternalSKU || false,
                  IsDevTest: sku.isDevTest ?? false,
                  SubscriptionBuyingModel: isBoth ? 'ALC' : sku.buyingModel,
                },
              },
            ],
          });

          // 2. EA SKU row (only when buyingModel === 'both')
          if (isBoth && eaSkuId !== null) {
            bulkEntry.push({
              table_key: 'skus',
              entries: [
                {
                  data: {
                    ID: eaSkuId,
                    Name: sku.eaSkuName
                      ? sanitizeTextInput(sku.eaSkuName)
                      : `${sanitizeTextInput(sku.skuName || '')}-EA`,
                    IsExternal: sku.isExternalSKU || false,
                    IsDevTest: sku.isDevTest ?? false,
                    SubscriptionBuyingModel: 'EA',
                  },
                },
              ],
            });
          }

          // 3. Config rows for ALC SKU
          buildSkuConfigEntries(currentSkuId);
          buildAddonEntries(currentSkuId);

          // 4. Config rows for EA SKU (exact copy, different SkuID)
          if (isBoth && eaSkuId !== null) {
            buildSkuConfigEntries(eaSkuId);
            buildAddonEntries(eaSkuId);
          }
        });
      }
    }

    return { bulk_entry: bulkEntry };
  };

  const handleSubmit = async () => {
    if (!formData) {return;}

    setSubmitting(true);
    try {
      // One request ID is generated for this submit and automatically
      // attached to every API call issued from within the block below
      // (via the ApiService request interceptor), so the backend can
      // correlate all resulting downstream work under one audit trail.
      const { requestId, result: response } = await apiService.runOperation(async () => {
        if (onboardingType === 'product') {
          // Product only onboarding - transform and use bulk insert API
          const bulkPayload = transformToBulkInsertPayload(formData, 'product');
          return apiService.bulkInsert(bulkPayload);
        }

        if (onboardingType === 'sku') {
          // SKU only onboarding
          if (!formData.isNewSKU && formData.existingSKUId && formData.skuOrgEntitlementConfigs && formData.skuOrgEntitlementConfigs.length > 0) {
            // Existing SKU with org entitlement configs - use bulk insert API
            const orgEntitlementConfigEntries = formData.skuOrgEntitlementConfigs.flatMap((config: Record<string, unknown>) =>
              (config.orgEntitlementIds as number[]).map((entId: number) => ({
                data: {
                  SkuID: formData.existingSKUId ?? 0,
                  OrgEntitlementID: entId
                }
              }))
            );

            const bulkPayload = {
              bulk_entry: [
                {
                  table_key: 'sku_org_entitlement_configs',
                  entries: orgEntitlementConfigEntries
                }
              ]
            };
            return apiService.bulkInsert(bulkPayload);
          }

          // New SKU onboarding - transform and use bulk insert API
          const bulkPayload = transformToBulkInsertPayload(formData, 'sku');
          return apiService.bulkInsert(bulkPayload);
        }

        // Combined product + SKU onboarding
        if (formData.isAddonSKU) {
          return apiService.onboardAddonSKU(formData);
        }
        if (formData.isNewProduct) {
          return apiService.onboardProduct(formData);
        }
        return apiService.onboardSKU(formData);
      });

      notify.success(`${response.message || 'Onboarding completed successfully!'} Request ID: ${requestId}`);
      form.resetFields();
      setFormData(null);
      setCurrentStep(0);
      setOnboardingType('both');
      setIsNewProduct(true);
      setIsNewSKU(true);
      setIsOrgEntitled(false);
      setSKUTypes({ 0: 'single' });
      setUseExistingEntitlements(false);
      setHasProvisioningQuestions(false);
      // Refresh data — reuse the same operation ID so every call in this
      // submit (mutation + follow-up refresh) shares one X-Request-ID.
      await apiService.runOperation(() => loadInitialData(), requestId);
    } catch (error: unknown) {
      const err = error as Record<string, { data?: { message?: string } }>;
      notify.error(err.response?.data?.message || 'Failed to complete onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const renderPreview = () => {
    if (!formData) {return null;}

    return (
      <Card>
        <Title level={2}>Review Your Configuration</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          Please review all the details before submitting.
        </Text>

        <Descriptions bordered column={1} size="small">
          {/* Onboarding Type */}
          <Descriptions.Item label="Onboarding Type">
            <Tag color="purple">
              {onboardingType === 'product' ? 'Product Only' :
                onboardingType === 'sku' ? 'SKU Only' :
                  'Product + SKU'}
            </Tag>
          </Descriptions.Item>

          {/* Product Details */}
          {(onboardingType === 'product' || onboardingType === 'both') && (
            <>
              <Descriptions.Item label="Product Type">
                <Tag color="blue">{formData.isNewProduct ? 'New Product' : 'Existing Product'}</Tag>
              </Descriptions.Item>

              {formData.isNewProduct ? (
                <>
                  <Descriptions.Item label="Product Name">{formData.productName}</Descriptions.Item>
                  <Descriptions.Item label="Product ID (New)">
                    <Tag color="blue">{formData.newProductId}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Product Key">{formData.productKey}</Descriptions.Item>
                  <Descriptions.Item label="Provisioning Client">{formData.provisioningClient}</Descriptions.Item>
                  <Descriptions.Item label="Provisioning Style">{formData.provisioningStyle}</Descriptions.Item>
                </>
              ) : (
                <Descriptions.Item label="Selected Product">
                  {products.find(p => p.id === formData.existingProductId)?.name || formData.existingProductId}
                </Descriptions.Item>
              )}
            </>
          )}

          {/* SKU Details */}
          {(onboardingType === 'sku' || onboardingType === 'both') && (
            <>
              <Descriptions.Item label="SKU Type">
                <Tag color="green">{formData.isNewSKU ? 'New SKU' : 'Existing SKU'}</Tag>
              </Descriptions.Item>

              {formData.isNewSKU && formData.skus && formData.skus.length > 0 ? (
                <>
                  {formData.skus.map((sku, skuIndex) => (
                    <React.Fragment key={skuIndex}>
                      <Descriptions.Item label={`SKU ${skuIndex + 1} - Name`}>
                        <Tag color="blue">{sku.skuName || 'Unnamed SKU'}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="External SKU">
                        {sku.isExternalSKU ? <Tag color="orange">Yes</Tag> : <Tag>No</Tag>}
                      </Descriptions.Item>
                      <Descriptions.Item label="Is Dev Test">
                        {sku.isDevTest ? <Tag color="orange">Yes</Tag> : <Tag>No</Tag>}
                      </Descriptions.Item>
                      <Descriptions.Item label="Buying Model">
                        <Tag color="purple">{sku.buyingModel === 'both' ? 'Both (ALC + EA)' : sku.buyingModel || 'Not specified'}</Tag>
                        {sku.buyingModel === 'both' && sku.eaSkuName && (
                          <span style={{ marginLeft: 8 }}>
                            EA Name: <Tag color="green">{sku.eaSkuName}</Tag>
                          </span>
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="SKU-Product Relationship">
                        <Tag color="cyan">
                          {sku.skuType === 'single'
                            ? 'Single Product (1:1)'
                            : sku.skuType === 'suite'
                              ? 'Suite (1:Many)'
                              : 'Add-on SKU'}
                        </Tag>
                      </Descriptions.Item>

                      {sku.skuType === 'addon' && sku.productConfigs && sku.productConfigs[0] && sku.productConfigs[0].productId && (
                        <>
                          <Descriptions.Item label="Base Product">
                            <Tag color="magenta">{products.find(p => p.id === sku.productConfigs?.[0].productId)?.name || sku.productConfigs?.[0].productId}</Tag>
                          </Descriptions.Item>
                          {sku.productConfigs?.[0].operation && <Descriptions.Item label="Operation"><Tag>{sku.productConfigs[0].operation}</Tag></Descriptions.Item>}
                          {sku.productConfigs?.[0].operand && <Descriptions.Item label="Operand"><Tag>{sku.productConfigs[0].operand}</Tag></Descriptions.Item>}
                          {sku.productConfigs?.[0].forceRegionName && <Descriptions.Item label="Force Region"><Tag color="orange">{sku.productConfigs[0].forceRegionName}</Tag></Descriptions.Item>}
                          {sku.productConfigs?.[0].allowedRegions && sku.productConfigs[0].allowedRegions.length > 0 && (
                            <Descriptions.Item label="Allowed Regions">
                              {sku.productConfigs[0].allowedRegions.map((r: string) => (
                                <Tag key={r} color="green">{r}</Tag>
                              ))}
                            </Descriptions.Item>
                          )}
                          {sku.addonType && <Descriptions.Item label="Add-on Type"><Tag color="purple">{sku.addonType}</Tag></Descriptions.Item>}
                          {sku.addonDescription && <Descriptions.Item label="Description"><Text>{sku.addonDescription}</Text></Descriptions.Item>}
                          {sku.productConfigs[0].entitlementIds && sku.productConfigs[0].entitlementIds.length > 0 && (
                            <Descriptions.Item label="Add-on Entitlements">
                              {sku.productConfigs[0].entitlementIds.map((id: number) => {
                                const ent = entitlements.find(e => e.id === id);
                                return ent ? (
                                  <Tag key={id} color="geekblue">
                                    {ent.name}: {ent.value}
                                  </Tag>
                                ) : null;
                              })}
                            </Descriptions.Item>
                          )}
                        </>
                      )}

                      {sku.skuType === 'single' && sku.productConfigs && sku.productConfigs[0] && (
                        <>
                          {sku.productConfigs[0].productId && (
                            <Descriptions.Item label="Linked Product">
                              <Tag color="blue">
                                {products.find(p => p.id === sku.productConfigs?.[0].productId)?.name || sku.productConfigs?.[0].productId}
                              </Tag>
                            </Descriptions.Item>
                          )}
                          {sku.productConfigs?.[0].operation && <Descriptions.Item label="Operation"><Tag>{sku.productConfigs[0].operation}</Tag></Descriptions.Item>}
                          {sku.productConfigs?.[0].operand && <Descriptions.Item label="Operand"><Tag>{sku.productConfigs[0].operand}</Tag></Descriptions.Item>}
                          {sku.productConfigs?.[0].forceRegionName && <Descriptions.Item label="Force Region"><Tag color="orange">{sku.productConfigs[0].forceRegionName}</Tag></Descriptions.Item>}
                          {sku.productConfigs[0].entitlementIds && sku.productConfigs[0].entitlementIds.length > 0 && (
                            <Descriptions.Item label="SKU Entitlements">
                              {sku.productConfigs[0].entitlementIds.map((id: number) => {
                                const ent = entitlements.find(e => e.id === id);
                                return ent ? (
                                  <Tag key={id} color="cyan">
                                    {ent.name}: {ent.value}
                                  </Tag>
                                ) : null;
                              })}
                            </Descriptions.Item>
                          )}
                        </>
                      )}

                      {sku.skuType === 'suite' && sku.productConfigs && sku.productConfigs.length > 0 && (
                        <Descriptions.Item label="Suite Products">
                          {sku.productConfigs.map((config, idx) => {
                            const product = products.find(p => p.id === config.productId);
                            const configEntitlements = config.entitlementIds?.map(id =>
                              entitlements.find(e => e.id === id)
                            ).filter(Boolean);

                            return (
                              <div key={idx} style={{ marginBottom: 12, paddingBottom: 8, borderBottom: idx < (sku.productConfigs?.length ?? 0) - 1 ? '1px solid var(--border-color)' : 'none' }}>
                                <Tag color="blue">{product?.name || config.productId}</Tag>
                                {config.operation && <span> • Operation: {config.operation}</span>}
                                {config.operand && <span> • Operand: {config.operand}</span>}
                                {config.forceRegionName && <span> • Region: {config.forceRegionName}</span>}
                                {configEntitlements && configEntitlements.length > 0 && (
                                  <div style={{ marginTop: 4, marginLeft: 8 }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>Entitlements: </Text>
                                    {configEntitlements.map((ent) => ent ? (
                                      <Tag key={ent.id} color="geekblue" style={{ marginTop: 2 }}>
                                        {ent.name}: {ent.value}
                                      </Tag>
                                    ) : null)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </Descriptions.Item>
                      )}

                      {skuIndex < (formData.skus?.length ?? 0) - 1 && <Descriptions.Item label=" "><div style={{ ...getDividerStyle(), margin: '8px 0' }} /></Descriptions.Item>}
                    </React.Fragment>
                  ))}
                </>
              ) : (
                <Descriptions.Item label="Selected SKU">
                  {skus.find(s => s.id === formData.existingSKUId)?.name || formData.existingSKUId}
                </Descriptions.Item>
              )}
            </>
          )}

          {/* Entitlements - Only for Product onboarding */}
          {(onboardingType === 'product' || onboardingType === 'both') && (
            <>
              <Descriptions.Item label="Entitlement Type">
                <Tag color={formData.isOrgEntitled ? 'magenta' : 'geekblue'}>
                  {formData.isOrgEntitled ? 'Org Entitled' : 'Standard Entitlements'}
                </Tag>
              </Descriptions.Item>

              {!formData.isOrgEntitled ? (
                <>
                  {formData.useExistingEntitlements ? (
                    <Descriptions.Item label="Selected Entitlements">
                      {formData.existingEntitlementIds?.map(id => {
                        const ent = entitlements.find(e => e.id === id);
                        return ent ? <Tag key={id}>{ent.name}: {ent.value}</Tag> : null;
                      })}
                    </Descriptions.Item>
                  ) : (
                    <Descriptions.Item label="New Entitlements">
                      {formData.entitlements?.map((ent, idx) => (
                        <div key={idx} style={{ marginBottom: 4 }}>
                          {ent.productId !== undefined && <Tag color="default">{products.find((product) => Number(product.id) === Number(ent.productId))?.name || ent.productId}</Tag>}
                          {formData.newEntitlementIds && formData.newEntitlementIds[idx] && (
                            <Tag color="blue">ID: {formData.newEntitlementIds[idx]}</Tag>
                          )}
                          <Tag color="blue">{ent.name}: {ent.value}</Tag>
                          {ent.needs_quantity && <span> (Needs Quantity{ent.unit ? `: ${ent.unit}` : ''})</span>}
                        </div>
                      ))}
                    </Descriptions.Item>
                  )}
                </>
              ) : (
                <>
                  {formData.useExistingOrgEntitlements ? (
                    <Descriptions.Item label="Selected Org Entitlements">
                      {formData.existingOrgEntitlementIds?.map(id => {
                        const ent = orgEntitlements.find(e => e.id === id);
                        return ent ? <Tag key={id}>{ent.name}: {ent.value} ({ent.access_type})</Tag> : null;
                      })}
                    </Descriptions.Item>
                  ) : (
                    <Descriptions.Item label="New Org Entitlements">
                      {formData.orgEntitlements?.map((ent, idx) => (
                        <div key={idx} style={{ marginBottom: 4 }}>
                          {ent.productId !== undefined && <Tag color="default">{products.find((product) => Number(product.id) === Number(ent.productId))?.name || ent.productId}</Tag>}
                          {formData.newOrgEntitlementIds && formData.newOrgEntitlementIds[idx] && (
                            <Tag color="magenta">ID: {formData.newOrgEntitlementIds[idx]}</Tag>
                          )}
                          <Tag color="magenta">{ent.name}: {ent.value}</Tag>
                          <span> • Access: {ent.access_type}</span>
                          {ent.needs_quantity && <span> • Needs Quantity{ent.unit ? `: ${ent.unit}` : ''}</span>}
                        </div>
                      ))}
                    </Descriptions.Item>
                  )}
                </>
              )}
            </>
          )}
          {(onboardingType === 'product' || onboardingType === 'both') && formData.isNewProduct && formData.allowedRegions && formData.allowedRegions.length > 0 && (
            <Descriptions.Item label="Allowed Regions">
              {formData.allowedRegions.map(regionCode => {
                const region = regions.find(r => r.region_code === regionCode);
                return <Tag key={regionCode} color="green">{region?.region_name || regionCode}</Tag>;
              })}
            </Descriptions.Item>
          )}

          {/* Provisioning Questions - Only for new products */}
          {(onboardingType === 'product' || onboardingType === 'both') && formData.isNewProduct && formData.hasProvisioningQuestions && formData.provisioningQuestions && formData.provisioningQuestions.length > 0 && (
            <Descriptions.Item label="Provisioning Questions">
              {formData.provisioningQuestions.map((q, idx) => (
                <div key={idx} style={{ marginBottom: 8 }}>
                  <Tag color="purple">{q.display_name || q.name}</Tag>
                  <span> • Type: {q.type}</span>
                  {q.description && <div style={{ marginLeft: 8, fontSize: '12px', ...getSecondaryTextStyle() }}>{q.description}</div>}
                </div>
              ))}
            </Descriptions.Item>
          )}
        </Descriptions>

        <Divider />

        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={handlePrevious} size="large">
            Back to Edit
          </Button>
          <Space>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={submitting}
              size="large"
              icon={<ArrowRightOutlined />}
            >
              Confirm & Submit
            </Button>
          </Space>
        </Space>
      </Card>
    );
  };

  // Step 0: Select Onboarding Type
  const renderOnboardingTypeStep = () => (
    <Card title="What would you like to onboard?">
      <Alert
        message="Choose your onboarding workflow"
        description="Select whether you want to onboard a product, SKU, or both together. This will determine the subsequent steps."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form.Item
        name="onboardingType"
        label="Onboarding Type"
        rules={[{ required: true, message: 'Please select onboarding type' }]}
      >
        <Radio.Group
          onChange={(e) => {
            const nextType = e.target.value;
            setOnboardingType(nextType);
            if (nextType === 'product' || nextType === 'both') {
              const defaultIsNewProduct = nextType === 'both';
              setIsNewProduct(defaultIsNewProduct);
              form.setFieldsValue({ isNewProduct: defaultIsNewProduct });
            }
          }}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card
              size="small"
              hoverable
              style={{
                cursor: 'pointer',
                border: onboardingType === 'product' ? '2px solid #1890ff' : '1px solid #d9d9d9'
              }}
            >
              <Radio value="product">
                <div style={{ marginLeft: 8 }}>
                  <Text strong style={{ fontSize: 16 }}>Product Only</Text>
                  <div style={{ marginTop: 4, ...getSecondaryTextStyle() }}>
                    Configure a new or existing product with entitlements, regions, and provisioning questions.
                    <div style={{ marginTop: 4 }}>
                      <Tag color="blue">Product Details</Tag>
                      <Tag color="geekblue">Entitlements</Tag>
                      <Tag color="green">Regions</Tag>
                      <Tag color="purple">Questions</Tag>
                    </div>
                  </div>
                </div>
              </Radio>
            </Card>

            <Card
              size="small"
              hoverable
              style={{
                cursor: 'pointer',
                border: onboardingType === 'sku' ? '2px solid #1890ff' : '1px solid #d9d9d9'
              }}
            >
              <Radio value="sku">
                <div style={{ marginLeft: 8 }}>
                  <Text strong style={{ fontSize: 16 }}>SKU Only</Text>
                  <div style={{ marginTop: 4, ...getSecondaryTextStyle() }}>
                    Onboard a standalone SKU (1:1) or suite SKU (1:Many) and link to existing products.
                    <div style={{ marginTop: 4 }}>
                      <Tag color="green">SKU Details</Tag>
                      <Tag color="cyan">Product Linking</Tag>
                      <Tag color="orange">Standalone or Suite</Tag>
                    </div>
                  </div>
                </div>
              </Radio>
            </Card>

            <Card
              size="small"
              hoverable
              style={{
                cursor: 'pointer',
                border: onboardingType === 'migrate' ? '2px solid #1890ff' : '1px solid #d9d9d9'
              }}
            >
              <Radio value="migrate">
                <div style={{ marginLeft: 8 }}>
                  <Text strong style={{ fontSize: 16 }}>Migrate/Clone SKUs</Text>
                  <div style={{ marginTop: 4, ...getSecondaryTextStyle() }}>
                    Convert existing SKUs to a different buying model, or clone them with a new name.
                    <div style={{ marginTop: 4 }}>
                      <Tag color="purple">Clone</Tag>
                      <Tag color="orange">ALC → EA</Tag>
                      <Tag color="blue">EA → ALC</Tag>
                    </div>
                  </div>
                </div>
              </Radio>
            </Card>

          </Space>
        </Radio.Group>
      </Form.Item>

      <Divider />
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Button type="primary" onClick={handleNext} size="large" icon={<ArrowRightOutlined />}>
          Continue
        </Button>
      </Space>
    </Card>
  );

  // Step 1: Product Information
  const renderProductStep = () => (
    <Card title="Product Information">
      <Form.Item name="isNewProduct" label="Product Type">
        <Radio.Group onChange={(e) => setIsNewProduct(e.target.value)}>
          <Radio value={false}>Existing Product</Radio>
          <Radio value={true}>New Product</Radio>
        </Radio.Group>
      </Form.Item>

      {/* Hidden field to store calculated newProductId */}
      <Form.Item name="newProductId" hidden>
        <Input />
      </Form.Item>

      {isNewProduct ? (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="productName"
                label="Product Name"
                rules={[{ required: true, message: 'Please enter product name' }, noLeadingTrailingSpaces]}
              >
                <Input placeholder="e.g., Cisco XDR, SASE, AI Defense" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="productKey"
                label="Product Key"
                rules={[{ required: true, message: 'Please enter product key' }, noLeadingTrailingSpaces]}
                extra="Lowercase identifier for the product (e.g., 'xdr' for Cisco XDR)"
              >
                <Input placeholder="e.g., xdr, sase, ai-defense" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="provisioningClient"
                label="Provisioning Client"
                rules={[{ required: true, message: 'Please select provisioning client' }]}
                extra="The client responsible for provisioning this product"
              >
                <Select placeholder="Select provisioning client">
                  <Option value="CLIENT_COMMON">CLIENT_COMMON</Option>
                  <Option value="CLIENT_XDR">CLIENT_XDR</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="provisioningStyle"
                label="Provisioning Style"
                rules={[{ required: true, message: 'Please select provisioning style' }]}
              >
                <Radio.Group>
                  <Radio value="STYLE_SYNC">STYLE_SYNC</Radio>
                  <Radio value="STYLE_ASYNC">STYLE_ASYNC</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Divider>Product Flags</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="is_hidden"
                label="Is Hidden"
                valuePropName="checked"
                extra="Hide this product from UI listings"
                tooltip={PRODUCT_FLAG_TOOLTIPS.IS_HIDDEN}
              >
                <Checkbox />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_auto_provision"
                label="Auto Provision"
                valuePropName="checked"
                extra="Enable automatic provisioning"
                tooltip={PRODUCT_FLAG_TOOLTIPS.IS_AUTO_PROVISION}
              >
                <Checkbox />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_composite"
                label="Is Composite"
                valuePropName="checked"
                extra="Product is composed of multiple services"
                tooltip={PRODUCT_FLAG_TOOLTIPS.IS_COMPOSITE}
              >
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="is_brownfield_only"
                label="Brownfield Only"
                valuePropName="checked"
                extra="Only for existing deployments"
                tooltip={PRODUCT_FLAG_TOOLTIPS.IS_BROWNFIELD_ONLY}
              >
                <Checkbox />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_unified_onboarding_supported"
                label="Unified Onboarding"
                valuePropName="checked"
                extra="Supports unified onboarding flow"
                tooltip={PRODUCT_FLAG_TOOLTIPS.IS_UNIFIED_ONBOARDING_SUPPORTED}
              >
                <Checkbox />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_parent_provisioning_supported"
                label="Parent Provisioning"
                valuePropName="checked"
                extra="Supports parent-based provisioning"
                tooltip={PRODUCT_FLAG_TOOLTIPS.IS_PARENT_PROVISIONING_SUPPORTED}
              >
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="inherit_parent_metadata"
                label="Inherit Parent Metadata"
                valuePropName="checked"
                extra="Inherit metadata from parent product"
                tooltip={PRODUCT_FLAG_TOOLTIPS.INHERIT_PARENT_METADATA}
              >
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>
        </>
      ) : (
        <Form.Item
          name="existingProductId"
          label="Select Existing Product"
          rules={[{ required: true, message: 'Please select a product' }]}
        >
          <Select
            placeholder="Select a product"
            showSearch
            filterOption={(input, option) =>
              (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(productId) => {
              // Auto-select entitlement type based on product's existing entitlements
              const hasStandardEntitlements = entitlements.some(ent => ent.product_id === productId);
              const hasOrgEntitlements = orgEntitlements.some(ent => ent.product_id === productId);

              if (hasOrgEntitlements) {
                // If product has org entitlements (with or without standard), select org entitled
                setIsOrgEntitled(true);
                form.setFieldsValue({ isOrgEntitled: true });
              } else if (hasStandardEntitlements && !hasOrgEntitlements) {
                // If only standard entitlements exist, select standard
                setIsOrgEntitled(false);
                form.setFieldsValue({ isOrgEntitled: false });
              }
            }}
          >
            {products.map((product) => (
              <Option key={product.id} value={product.id}>
                {product.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Divider />
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handlePrevious} size="large">
          Previous
        </Button>
        <Button type="primary" onClick={handleNext} size="large" icon={<ArrowRightOutlined />}>
          Next: SKU Configuration
        </Button>
      </Space>
    </Card>
  );

  // Step 2: SKU Configuration
  const renderSKUStep = () => (
    <Card title="SKU Configuration">
      {onboardingType === 'sku' && (
        <Alert
          message="SKU-Only Onboarding"
          description="You're onboarding SKUs. You can add multiple SKUs and link them to existing products."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div>
        <Form.Item name="isNewSKU" label="SKU Type">
          <Radio.Group onChange={(e) => {
            setIsNewSKU(e.target.value);
            if (e.target.value) {
              form.setFieldsValue({ existingSKUId: undefined });
            } else {
              form.setFieldsValue({
                skus: [{
                  skuName: '',
                  isExternalSKU: false,
                  isDevTest: false,
                  buyingModel: undefined,
                  skuType: 'single',
                  productConfigs: [{}]
                }]
              });
              setSKUTypes({ 0: 'single' });
            }
          }}>
            <Radio value={true}>New SKU</Radio>
            <Radio value={false}>Existing SKU</Radio>
          </Radio.Group>
        </Form.Item>

        {isNewSKU ? (
          <>
            <Form.List name="skus">
              {(fields, { add, remove }) => (
                <>
                  <Title level={5} style={{ marginBottom: 16 }}>SKU Configuration</Title>

                  {fields.map(({ key, name, ...restField }, index) => {
                    const currentSKUType = skuTypes[index] || 'single';

                    return (
                      <div key={key} style={{ marginBottom: 24 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                          <thead>
                            <tr style={getTableHeaderStyle()}>
                              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, minWidth: 200 }}>SKU Name * <br /><span style={{ fontWeight: 400, fontSize: 11, opacity: 0.7 }}>(EA name appears below when Both selected)</span></th>
                              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, width: 100 }}>External</th>
                              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, width: 100 }}>Is Dev Test</th>
                              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, width: 120 }}>Buying Model *</th>
                              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, width: 150 }}>Relationship *</th>
                              <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, width: 80 }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr style={getTableRowStyle()}>
                              <td style={{ padding: '8px' }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'skuName']}
                                  rules={[{ required: true, message: 'Required' }, noLeadingTrailingSpaces]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input placeholder="ALC SKU name" />
                                </Form.Item>
                                {/* EA SKU name — shown inline when "Both" buying model is selected */}
                                <Form.Item noStyle shouldUpdate={(prev, curr) =>
                                  prev.skus?.[index]?.buyingModel !== curr.skus?.[index]?.buyingModel
                                }>
                                  {({ getFieldValue }) =>
                                    getFieldValue(['skus', index, 'buyingModel']) === 'both' ? (
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'eaSkuName']}
                                        rules={[{ required: true, message: 'EA name required' }, noLeadingTrailingSpaces]}
                                        style={{ marginBottom: 0, marginTop: 6 }}
                                      >
                                        <Input placeholder="EA SKU name" />
                                      </Form.Item>
                                    ) : null
                                  }
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'isExternalSKU']}
                                  valuePropName="checked"
                                  style={{ marginBottom: 0 }}
                                >
                                  <Checkbox />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'isDevTest']}
                                  valuePropName="checked"
                                  style={{ marginBottom: 0 }}
                                >
                                  <Checkbox aria-label={`Is Dev Test for SKU ${index + 1}`} />
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'buyingModel']}
                                  rules={[{ required: true, message: 'Required' }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Select
                                    placeholder="Select model"
                                    onChange={(value) => {
                                      // Clear eaSkuName if switching away from "both"
                                      if (value !== 'both') {
                                        const formValues = form.getFieldsValue();
                                        if (formValues.skus?.[index]) {
                                          formValues.skus[index].eaSkuName = undefined;
                                          form.setFieldsValue(formValues);
                                        }
                                      }
                                    }}
                                  >
                                    <Option value="EA">EA</Option>
                                    <Option value="ALC">ALC</Option>
                                    <Option value="both">Both (ALC + EA)</Option>
                                  </Select>
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'skuType']}
                                  rules={[{ required: true, message: 'Required' }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Select
                                    placeholder="Select type"
                                    onChange={(value) => {
                                      setSKUTypes(prev => ({ ...prev, [index]: value }));
                                      // Reset fields based on type
                                      const formValues = form.getFieldsValue();
                                      if (formValues.skus && formValues.skus[index]) {
                                        const currentSku = formValues.skus[index];
                                        if (value === 'single') {
                                          formValues.skus[index] = {
                                            ...currentSku,
                                            productConfigs: []
                                          };
                                        } else if (value === 'suite') {
                                          formValues.skus[index] = {
                                            ...currentSku,
                                            productConfigs: []
                                          };
                                        } else if (value === 'addon') {
                                          formValues.skus[index] = {
                                            ...currentSku,
                                            productConfigs: []
                                          };
                                        }
                                        form.setFieldsValue(formValues);
                                      }
                                    }}
                                  >
                                    <Option value="single">Standalone (Single Product SKU)</Option>
                                    <Option value="suite">Suite (Multi Product SKU)</Option>
                                    <Option value="addon">Add-on</Option>
                                  </Select>
                                </Form.Item>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                {fields.length > 1 && (
                                  <Button
                                    type="link"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                      remove(name);
                                      // Update skuTypes state
                                      setSKUTypes(prev => {
                                        const newTypes = { ...prev };
                                        delete newTypes[index];
                                        // Reindex remaining items
                                        const reindexed: typeof newTypes = {};
                                        Object.keys(newTypes).forEach((key, _idx) => {
                                          const numKey = parseInt(key);
                                          if (numKey > index) {
                                            reindexed[numKey - 1] = newTypes[numKey];
                                          } else if (numKey < index) {
                                            reindexed[numKey] = newTypes[numKey];
                                          }
                                        });
                                        return reindexed;
                                      });
                                    }}
                                  />
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>

                        <Card
                          size="small"
                          title={
                            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.skus?.[index]?.skuName !== curr.skus?.[index]?.skuName}>
                              {({ getFieldValue }) => {
                                const skuName = getFieldValue(['skus', index, 'skuName']);
                                return `Configuration: ${skuName || 'Unnamed SKU'}`;
                              }}
                            </Form.Item>
                          }
                          style={{ marginBottom: 24 }}
                        >
                          {currentSKUType === 'single' && onboardingType === 'sku' && (
                            <>
                              <Text strong style={{ display: 'block', marginBottom: 8 }}>Configure Standalone SKU:</Text>
                              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                                <thead>
                                  <tr style={{ ...getTableHeaderStyle() }}>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, minWidth: 150 }}>Product *</th>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, width: 100 }}>Operation</th>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, width: 80 }}>Operand</th>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, width: 120 }}>Force Region</th>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, minWidth: 150 }}>Entitlements *</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr style={{ ...getTableRowStyle() }}>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item
                                        name={[name, 'productConfigs', 0, 'productId']}
                                        rules={[{ required: true, message: 'Required' }]}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Select
                                          placeholder="Select product"
                                          showSearch
                                          size="small"
                                          filterOption={(input, option) =>
                                            (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
                                          }
                                        >
                                          {products.map((product) => (
                                            <Option key={product.id} value={product.id}>
                                              {product.name}
                                            </Option>
                                          ))}
                                        </Select>
                                      </Form.Item>
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item
                                        name={[name, 'productConfigs', 0, 'operation']}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Select placeholder="Operation" size="small">
                                          <Option value="ADD">ADD</Option>
                                          <Option value="MULTIPLY">MULTIPLY</Option>
                                        </Select>
                                      </Form.Item>
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item
                                        name={[name, 'productConfigs', 0, 'operand']}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Input placeholder="Value" size="small" />
                                      </Form.Item>
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item
                                        name={[name, 'productConfigs', 0, 'forceRegionName']}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Input placeholder="Region" size="small" />
                                      </Form.Item>
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => {
                                        const prevProductId = prevValues?.skus?.[index]?.productConfigs?.[0]?.productId;
                                        const currProductId = currentValues?.skus?.[index]?.productConfigs?.[0]?.productId;
                                        return prevProductId !== currProductId;
                                      }}>
                                        {({ getFieldValue }) => {
                                          const selectedProductId = getFieldValue(['skus', index, 'productConfigs', 0, 'productId']);
                                          const filteredEntitlements = selectedProductId
                                            ? entitlements.filter(e => e.product_id === selectedProductId)
                                            : [];

                                          return (
                                            <Form.Item
                                              name={[name, 'productConfigs', 0, 'entitlementIds']}
                                              rules={[{ required: true, message: 'Required' }]}
                                              style={{ marginBottom: 0 }}
                                            >
                                              <Select
                                                mode="multiple"
                                                placeholder="Select entitlements"
                                                popupMatchSelectWidth={false}
                                                dropdownStyle={{ maxWidth: 600, whiteSpace: "normal", wordBreak: "break-word" }}
                                                tagRender={entitlementTagRender(filteredEntitlements)}
                                                showSearch
                                                size="small"
                                                disabled={!selectedProductId}
                                              >
                                                {filteredEntitlements.map((ent) => (
                                                  <Option key={ent.id} value={ent.id}>
                                                    {formatEntitlementLabel(ent)}
                                                  </Option>
                                                ))}
                                              </Select>
                                            </Form.Item>
                                          );
                                        }}
                                      </Form.Item>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </>
                          )}

                          {currentSKUType === 'suite' && (
                            <Form.List name={[name, 'productConfigs']}>
                              {(productFields, { add: addProduct, remove: removeProduct }) => (
                                <>
                                  <div style={{ marginBottom: 8 }}>
                                    <Text strong>Configure Products in Suite:</Text>
                                  </div>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                                    <thead>
                                      <tr style={getTableHeaderStyle()}>
                                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, minWidth: 150 }}>Product *</th>
                                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, width: 100 }}>Operation</th>
                                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, width: 80 }}>Operand</th>
                                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, width: 120 }}>Force Region</th>
                                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, minWidth: 150 }}>Entitlements</th>
                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, width: 60 }}>Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {productFields.map(({ key: prodKey, name: prodName, ...restProdField }, prodIndex) => (
                                        <tr key={prodKey} style={{ ...getTableRowStyle() }}>
                                          <td style={{ padding: '4px' }}>
                                            <Form.Item
                                              {...restProdField}
                                              name={[prodName, 'productId']}
                                              rules={[{ required: true, message: 'Required' }]}
                                              style={{ marginBottom: 0 }}
                                            >
                                              <Select
                                                placeholder="Select product"
                                                showSearch
                                                size="small"
                                                filterOption={(input, option) =>
                                                  (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
                                                }
                                              >
                                                {products.map((product) => (
                                                  <Option key={product.id} value={product.id}>
                                                    {product.name}
                                                  </Option>
                                                ))}
                                              </Select>
                                            </Form.Item>
                                          </td>
                                          <td style={{ padding: '4px' }}>
                                            <Form.Item
                                              {...restProdField}
                                              name={[prodName, 'operation']}
                                              style={{ marginBottom: 0 }}
                                            >
                                              <Select placeholder="Operation" size="small">
                                                <Option value="ADD">ADD</Option>
                                                <Option value="MULTIPLY">MULTIPLY</Option>
                                              </Select>
                                            </Form.Item>
                                          </td>
                                          <td style={{ padding: '4px' }}>
                                            <Form.Item
                                              {...restProdField}
                                              name={[prodName, 'operand']}
                                              style={{ marginBottom: 0 }}
                                            >
                                              <Input placeholder="Value" size="small" />
                                            </Form.Item>
                                          </td>
                                          <td style={{ padding: '4px' }}>
                                            <Form.Item
                                              {...restProdField}
                                              name={[prodName, 'forceRegionName']}
                                              style={{ marginBottom: 0 }}
                                            >
                                              <Input placeholder="Region" size="small" />
                                            </Form.Item>
                                          </td>
                                          <td style={{ padding: '4px' }}>
                                            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => {
                                              const prevProductId = prevValues?.skus?.[index]?.productConfigs?.[prodIndex]?.productId;
                                              const currProductId = currentValues?.skus?.[index]?.productConfigs?.[prodIndex]?.productId;
                                              return prevProductId !== currProductId;
                                            }}>
                                              {({ getFieldValue }) => {
                                                const selectedProductId = getFieldValue(['skus', index, 'productConfigs', prodIndex, 'productId']);
                                                const filteredEntitlements = selectedProductId
                                                  ? entitlements.filter(e => e.product_id === selectedProductId)
                                                  : [];

                                                return (
                                                  <Form.Item
                                                    {...restProdField}
                                                    name={[prodName, 'entitlementIds']}
                                                    style={{ marginBottom: 0 }}
                                                  >
                                                    <Select
                                                      mode="multiple"
                                                      placeholder="Select entitlements"
                                                      popupMatchSelectWidth={false}
                                                      dropdownStyle={{ maxWidth: 600, whiteSpace: "normal", wordBreak: "break-word" }}
                                                      tagRender={entitlementTagRender(filteredEntitlements)}
                                                      showSearch
                                                      size="small"
                                                      disabled={!selectedProductId}
                                                    >
                                                      {filteredEntitlements.map((ent) => (
                                                        <Option key={ent.id} value={ent.id}>
                                                          {formatEntitlementLabel(ent)}
                                                        </Option>
                                                      ))}
                                                    </Select>
                                                  </Form.Item>
                                                );
                                              }}
                                            </Form.Item>
                                          </td>
                                          <td style={{ padding: '4px', textAlign: 'center' }}>
                                            <Button
                                              type="link"
                                              danger
                                              size="small"
                                              icon={<MinusCircleOutlined />}
                                              onClick={() => removeProduct(prodName)}
                                            />
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  <div style={{ marginBottom: 16 }}>
                                    <Button
                                      type="primary"
                                      onClick={() => addProduct({})}
                                      icon={<PlusOutlined />}
                                      size="small"
                                    >
                                    Add Product in Suite
                                    </Button>
                                  </div>
                                </>
                              )}
                            </Form.List>
                          )}

                          {currentSKUType === 'addon' && onboardingType === 'sku' && (
                            <>
                              <Text strong style={{ display: 'block', marginBottom: 8 }}>Configure Add-on SKU:</Text>
                              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                                <thead>
                                  <tr style={{ ...getTableHeaderStyle() }}>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, minWidth: 150 }}>Base Product *</th>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, width: 100 }}>Operation</th>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, width: 80 }}>Operand</th>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, width: 120 }}>Force Region</th>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, minWidth: 200 }}>Allowed Regions</th>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, minWidth: 150 }}>Add-on Entitlements *</th>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, minWidth: 120 }}>Add-on Type *</th>
                                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, minWidth: 150 }}>Description</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr style={{ ...getTableRowStyle() }}>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item
                                        name={[name, 'productConfigs', 0, 'productId']}
                                        rules={[{ required: true, message: 'Required' }]}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Select
                                          placeholder="Select base product"
                                          showSearch
                                          size="small"
                                          filterOption={(input, option) =>
                                            (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
                                          }
                                        >
                                          {products.map((product) => (
                                            <Option key={product.id} value={product.id}>
                                              {product.name}
                                            </Option>
                                          ))}
                                        </Select>
                                      </Form.Item>
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item
                                        name={[name, 'productConfigs', 0, 'operation']}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Select placeholder="Operation" size="small">
                                          <Option value="ADD">ADD</Option>
                                          <Option value="MULTIPLY">MULTIPLY</Option>
                                        </Select>
                                      </Form.Item>
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item
                                        name={[name, 'productConfigs', 0, 'operand']}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Input placeholder="Value" size="small" />
                                      </Form.Item>
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item
                                        name={[name, 'productConfigs', 0, 'forceRegionName']}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Input placeholder="Region" size="small" />
                                      </Form.Item>
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item
                                        name={[name, 'productConfigs', 0, 'allowedRegions']}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Select
                                          mode="multiple"
                                          placeholder="Select regions"
                                          size="small"
                                          style={{ minWidth: 180 }}
                                        >
                                          {['NAM', 'APJC', 'US', 'CA', 'AU', 'IN', 'JP', 'UAE', 'EU'].map((region) => (
                                            <Option key={region} value={region}>{region}</Option>
                                          ))}
                                        </Select>
                                      </Form.Item>
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => {
                                        const prevProductId = prevValues?.skus?.[index]?.productConfigs?.[0]?.productId;
                                        const currProductId = currentValues?.skus?.[index]?.productConfigs?.[0]?.productId;
                                        return prevProductId !== currProductId;
                                      }}>
                                        {({ getFieldValue }) => {
                                          const selectedProductId = getFieldValue(['skus', index, 'productConfigs', 0, 'productId']);
                                          const filteredEntitlements = selectedProductId
                                            ? entitlements.filter(e => e.product_id === selectedProductId)
                                            : [];

                                          return (
                                            <Form.Item
                                              name={[name, 'productConfigs', 0, 'entitlementIds']}
                                              rules={[{ required: true, message: 'Required' }]}
                                              style={{ marginBottom: 0 }}
                                            >
                                              <Select
                                                mode="multiple"
                                                placeholder="Select entitlements"
                                                popupMatchSelectWidth={false}
                                                dropdownStyle={{ maxWidth: 600, whiteSpace: "normal", wordBreak: "break-word" }}
                                                tagRender={entitlementTagRender(filteredEntitlements)}
                                                showSearch
                                                size="small"
                                                disabled={!selectedProductId}
                                              >
                                                {filteredEntitlements.map((ent) => (
                                                  <Option key={ent.id} value={ent.id}>
                                                    {formatEntitlementLabel(ent)}
                                                  </Option>
                                                ))}
                                              </Select>
                                            </Form.Item>
                                          );
                                        }}
                                      </Form.Item>
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item
                                        name={[name, 'addonType']}
                                        rules={[{ required: true, message: 'Required' }]}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Select placeholder="Select type" size="small">
                                          <Option value="BASE_PRODUCT_ADDON">BASE_PRODUCT_ADDON</Option>
                                          <Option value="SUITES_ADDON">SUITES_ADDON</Option>
                                        </Select>
                                      </Form.Item>
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                      <Form.Item
                                        name={[name, 'addonDescription']}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Input placeholder="Description" size="small" />
                                      </Form.Item>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </>
                          )}
                        </Card>
                      </div>
                    );
                  })}

                  <Button
                    type="dashed"
                    onClick={() => {
                      const newIndex = fields.length;
                      add({
                        skuName: '',
                        isExternalSKU: false,
                        isDevTest: false,
                        buyingModel: undefined,
                        skuType: 'single',
                        productConfigs: [{}]
                      });
                      setSKUTypes(prev => ({ ...prev, [newIndex]: 'single' }));
                    }}
                    icon={<PlusOutlined />}
                    block
                    style={{ marginBottom: 24 }}
                  >
                  Add SKU
                  </Button>
                </>
              )}
            </Form.List>
          </>
        ) : (
          <>
            <Form.Item
              name="existingSKUId"
              label="Select Existing SKU"
              rules={[{ required: true, message: 'Please select an existing SKU' }]}
            >
              <Select
                placeholder="Select SKU"
                showSearch
                filterOption={(input, option) =>
                  (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {skus.map((sku) => (
                  <Option key={sku.id} value={sku.id}>
                    {sku.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
              prevValues?.existingSKUId !== currentValues?.existingSKUId
            }>
              {({ getFieldValue }) => {
                const selectedSKUId = getFieldValue('existingSKUId');

                if (!selectedSKUId) {
                  return null;
                }

                return (
                  <>
                    <Divider>Configure Org Entitlement Mappings</Divider>
                    <Alert
                      message="Dependent Service Configuration"
                      description="Select a dependent service product (org-entitled) and its entitlements to create SKU org entitlement configurations."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Form.List name="skuOrgEntitlementConfigs">
                      {(fields, { add, remove }) => (
                        <>
                          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                            <thead>
                              <tr style={getTableHeaderStyle()}>
                                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, minWidth: 200 }}>Dependent Service Product *</th>
                                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, minWidth: 200 }}>Org Entitlements *</th>
                                <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, width: 80 }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {fields.map(({ key, name, ...restField }) => (
                                <tr key={key} style={getTableRowStyle()}>
                                  <td style={{ padding: '4px' }}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'productId']}
                                      rules={[{ required: true, message: 'Required' }]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Select
                                        placeholder="Select org-entitled product"
                                        showSearch
                                        size="small"
                                        filterOption={(input, option) =>
                                          (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
                                        }
                                      >
                                        {products.filter(product =>
                                          orgEntitlements.some(ent => ent.product_id === product.id)
                                        ).map((product) => (
                                          <Option key={product.id} value={product.id}>
                                            {product.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Form.Item>
                                  </td>
                                  <td style={{ padding: '4px' }}>
                                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => {
                                      const prevProductId = prevValues?.skuOrgEntitlementConfigs?.[name]?.productId;
                                      const currProductId = currentValues?.skuOrgEntitlementConfigs?.[name]?.productId;
                                      return prevProductId !== currProductId;
                                    }}>
                                      {({ getFieldValue }) => {
                                        const selectedProductId = getFieldValue(['skuOrgEntitlementConfigs', name, 'productId']);
                                        const filteredOrgEntitlements = selectedProductId
                                          ? orgEntitlements.filter(e => e.is_service && e.product_id === selectedProductId)
                                          : [];

                                        return (
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'orgEntitlementIds']}
                                            rules={[{ required: true, message: 'Required' }]}
                                            style={{ marginBottom: 0 }}
                                          >
                                            <Select
                                              mode="multiple"
                                              placeholder="Select org entitlements"
                                              popupMatchSelectWidth={false}
                                              dropdownStyle={{ maxWidth: 600, whiteSpace: "normal", wordBreak: "break-word" }}
                                              tagRender={entitlementTagRender(filteredOrgEntitlements)}
                                              showSearch
                                              size="small"
                                              disabled={!selectedProductId}
                                            >
                                              {filteredOrgEntitlements.map((ent) => (
                                                <Option key={ent.id} value={ent.id}>
                                                  {formatEntitlementLabel(ent)}
                                                </Option>
                                              ))}
                                            </Select>
                                          </Form.Item>
                                        );
                                      }}
                                    </Form.Item>
                                  </td>
                                  <td style={{ padding: '4px', textAlign: 'center' }}>
                                    <Button
                                      type="link"
                                      danger
                                      size="small"
                                      icon={<MinusCircleOutlined />}
                                      onClick={() => remove(name)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <Button
                            type="primary"
                            onClick={() => add()}
                            icon={<PlusOutlined />}
                            size="small"
                          >
                          Add Org Entitlement Config
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </>
                );
              }}
            </Form.Item>
          </>
        )}

        <Divider />
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={handlePrevious} size="large">
          Previous
          </Button>
          <Button type="primary" onClick={handleNext} size="large" icon={<ArrowRightOutlined />}>
          Next: Entitlements
          </Button>
        </Space>
      </div>
    </Card>
  );

  // Step 3: Entitlements
  const renderEntitlementsStep = () => (
    <Card title="Entitlement Configuration">
      {/* Hidden fields to store calculated IDs */}
      <Form.Item name="newEntitlementIds" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="newOrgEntitlementIds" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="lastIndices" hidden>
        <Input />
      </Form.Item>

      <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
        prevValues?.existingProductId !== currentValues?.existingProductId ||
        prevValues?.isNewProduct !== currentValues?.isNewProduct
      }>
        {({ getFieldValue }) => {
          const existingProductId = getFieldValue('existingProductId');
          const currentIsNewProduct = getFieldValue('isNewProduct');

          // Check what entitlements exist for the selected product
          const hasStandardEntitlements = !currentIsNewProduct && existingProductId
            ? entitlements.some(ent => ent.product_id === existingProductId)
            : false;
          const hasOrgEntitlements = !currentIsNewProduct && existingProductId
            ? orgEntitlements.some(ent => ent.product_id === existingProductId)
            : false;

          // Determine which options should be disabled
          // When org entitlements exist (with or without standard), disable standard entitlements option
          const disableStandardEntitlements = !currentIsNewProduct && hasOrgEntitlements;
          // When only standard entitlements exist (no org entitlements), disable org entitled option
          const disableOrgEntitlements = !currentIsNewProduct && hasStandardEntitlements && !hasOrgEntitlements;

          return (
            <Form.Item name="isOrgEntitled" label="Entitlement Type">
              <Radio.Group onChange={(e) => setIsOrgEntitled(e.target.value)}>
                <Radio value={false} disabled={disableStandardEntitlements}>
                  Standard Entitlements
                  {disableStandardEntitlements && <Text type="secondary" style={{ marginLeft: 8 }}>(Use org entitlements for this product)</Text>}
                </Radio>
                <Radio value={true} disabled={disableOrgEntitlements}>
                  Org Entitled Product
                  {disableOrgEntitlements && <Text type="secondary" style={{ marginLeft: 8 }}>(Not available - product has only standard entitlements)</Text>}
                </Radio>
              </Radio.Group>
            </Form.Item>
          );
        }}
      </Form.Item>

      <Alert
        message={
          isOrgEntitled
            ? 'Org entitled products will insert into both entitlements and org_entitlements tables'
            : 'Standard entitlements will only insert into the entitlements table'
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Modal
        title={`Import ${entitlementImportMode === 'org' ? 'Org ' : ''}Entitlements`}
        open={entitlementImportOpen}
        onCancel={() => setEntitlementImportOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setEntitlementImportOpen(false)}>Cancel</Button>,
          <Button key="replace" onClick={() => applyEntitlementImport(false)} disabled={!entitlementImportText.trim()}>Replace list</Button>,
          <Button key="append" type="primary" onClick={() => applyEntitlementImport(true)} disabled={!entitlementImportText.trim()}>Append rows</Button>,
        ]}
        width={680}
        destroyOnHidden
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text>
            Importing into the {entitlementImportMode === 'org' ? 'org ' : ''}entitlement list. Choose a CSV file or paste spreadsheet rows below.
            Replace list overwrites the current rows; Append rows keeps existing filled rows and adds the import.
          </Text>
          <Text type="secondary">
            Required columns: <code>Name, Value</code>. Optional columns: <code>Product, Description, Require Description, Needs Quantity, Unit</code>
            {entitlementImportMode === 'org' && <>; org columns: <code>Access Type, Is Service</code> (Access Type defaults to RW)</>}.
            Product may be an exact product name, key, or ID. When omitted, rows use the product selected in this wizard. Rows for other products are skipped with a warning; at least one row must match the selected product. Duplicate Name/Value pairs are checked within each product. Header matching ignores capitalization, spaces, and underscores.
          </Text>
          <Upload
            accept=".csv,.tsv,text/csv,text/tab-separated-values"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              const reader = new FileReader();
              reader.onload = () => {
                setEntitlementImportText(String(reader.result || ''));
                setEntitlementImportErrors([]);
              };
              reader.onerror = () => setEntitlementImportErrors(['Could not read this file.']);
              reader.readAsText(file);
              return false;
            }}
          >
            <Button>Choose CSV/TSV file</Button>
          </Upload>
          <TextArea
            rows={9}
            value={entitlementImportText}
            onChange={(event) => {
              setEntitlementImportText(event.target.value);
              setEntitlementImportErrors([]);
            }}
            placeholder={'Name,Value,Description,Needs Quantity,Unit\nfeature_access,premium,Premium feature access,false,\nuser_limit,100,Maximum users,true,users'}
          />
          {entitlementImportText.trim() && (() => {
            const preview = parseEntitlementCSV(entitlementImportText, entitlementImportMode, getEntitlementImportOptions());
            const errors = entitlementImportErrors.length > 0 ? entitlementImportErrors : preview.errors;
            return errors.length > 0 ? (
              <Alert
                type="error"
                showIcon
                message="Import needs correction"
                description={<ul style={{ margin: 0, paddingLeft: 20 }}>
                  {errors.map((error, index) => <li key={`error-${index}-${error}`}>{error}</li>)}
                  {preview.warnings?.map((warning, index) => <li key={`warning-${index}-${warning}`}>{warning}</li>)}
                </ul>}
              />
            ) : preview.warnings?.length ? (
              <Alert
                type="warning"
                showIcon
                message={`${preview.rows.length} entitlement${preview.rows.length === 1 ? '' : 's'} ready to import; other product rows will be skipped`}
                description={<ul style={{ margin: 0, paddingLeft: 20 }}>{preview.warnings.map((warning, index) => <li key={`${index}-${warning}`}>{warning}</li>)}</ul>}
              />
            ) : (
              <Alert type="success" showIcon message={`${preview.rows.length} entitlement${preview.rows.length === 1 ? '' : 's'} ready to import`} />
            );
          })()}
        </Space>
      </Modal>

      {!isOrgEntitled ? (
        <>
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
            prevValues?.existingProductId !== currentValues?.existingProductId ||
            prevValues?.isNewProduct !== currentValues?.isNewProduct
          }>
            {({ getFieldValue }) => {
              const existingProductId = getFieldValue('existingProductId');
              const currentIsNewProduct = getFieldValue('isNewProduct');

              // Check if product has org entitlements
              const hasOrgEntitlements = !currentIsNewProduct && existingProductId
                ? orgEntitlements.some(ent => ent.product_id === existingProductId)
                : false;

              // Hide checkbox if it's a new product or if existing product has no org entitlements
              const shouldHideCheckbox = currentIsNewProduct || (!currentIsNewProduct && !hasOrgEntitlements);

              return shouldHideCheckbox ? null : (
                <Form.Item name="useExistingEntitlements" valuePropName="checked">
                  <Checkbox
                    onChange={(e) => setUseExistingEntitlements(e.target.checked)}
                  >
                    Use Existing Entitlements
                  </Checkbox>
                </Form.Item>
              );
            }}
          </Form.Item>

          {useExistingEntitlements ? (
            <Form.Item name="existingEntitlementIds" label="Select Entitlements">
              <Select
                mode="multiple"
                placeholder="Select entitlements"
                popupMatchSelectWidth={false}
                dropdownStyle={{ maxWidth: 600, whiteSpace: "normal", wordBreak: "break-word" }}
                tagRender={entitlementTagRender(entitlements)}
                showSearch
                filterOption={(input, option) =>
                  (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {entitlements.filter((ent) => ent.product_id === formData?.existingProductId).map((ent) => (
                  <Option key={ent.id} value={ent.id}>
                    {formatEntitlementLabel(ent)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <Form.List name="entitlements">
              {(fields, { add, remove }) => (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    <Button onClick={openEntitlementImport}>Import / paste CSV</Button>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                    <thead>
                      <tr style={{ ...getTableHeaderStyle() }}>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Product</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Name *</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Value *</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, width: 110 }} title="Require description before submitting. Uncheck to allow empty description (entitlement will not appear on SCC UI).">Req. Desc</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Needs Quantity</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Unit</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, width: 60 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map(({ key, name, ...restField }) => (
                        <React.Fragment key={key}>
                          <Form.Item
                            noStyle
                            shouldUpdate={(prev, curr) =>
                              prev?.entitlements?.[name]?.requireDescription !== curr?.entitlements?.[name]?.requireDescription
                            }
                          >
                            {({ getFieldValue: getVal }) => {
                              const reqDesc = getVal(['entitlements', name, 'requireDescription']);
                              const isRequired = reqDesc !== false;
                              return !isRequired ? (
                                <tr>
                                  <td colSpan={8} style={{ padding: '7px 8px 2px', borderBottom: 'none' }}>
                                    <Alert
                                      message="⚠️ No description — this entitlement will not appear on SCC UI"
                                      type="warning"
                                      style={{ fontSize: 12, padding: '4px 10px', borderRadius: 4, marginBottom: 2 }}
                                    />
                                  </td>
                                </tr>
                              ) : null;
                            }}
                          </Form.Item>
                          <tr style={{ ...getTableRowStyle() }}>
                            <td style={{ padding: '8px' }}>
                              <Form.Item noStyle shouldUpdate={(prev, curr) =>
                                prev?.entitlements?.[name]?.productId !== curr?.entitlements?.[name]?.productId ||
                                prev?.existingProductId !== curr?.existingProductId ||
                                prev?.newProductId !== curr?.newProductId ||
                                prev?.productName !== curr?.productName ||
                                prev?.isNewProduct !== curr?.isNewProduct
                              }>
                                {({ getFieldValue: getVal }) => {
                                  const productId = getVal(['entitlements', name, 'productId']) ??
                                    (getVal('isNewProduct') ? getVal('newProductId') : getVal('existingProductId'));
                                  const product = products.find((item) => Number(item.id) === Number(productId));
                                  return <Text>{product?.name || (Number(productId) === Number(getVal('newProductId')) ? getVal('productName') : '') || productId || 'Current product'}</Text>;
                                }}
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'name']}
                                rules={[{ required: true, message: 'Required' }, noLeadingTrailingSpaces]}
                                style={{ marginBottom: 0 }}
                              >
                                <Input placeholder="e.g., storage_limit" />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'value']}
                                rules={[{ required: true, message: 'Required' }, noLeadingTrailingSpaces]}
                                style={{ marginBottom: 0 }}
                              >
                                <Input placeholder="e.g., 1TB" />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <Form.Item
                                noStyle
                                shouldUpdate={(prev, curr) =>
                                  prev?.entitlements?.[name]?.requireDescription !== curr?.entitlements?.[name]?.requireDescription
                                }
                              >
                                {({ getFieldValue: getVal }) => {
                                  const reqDesc = getVal(['entitlements', name, 'requireDescription']);
                                  const isRequired = reqDesc !== false;
                                  return (
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'description']}
                                      rules={isRequired ? [{ required: true, message: 'Description is required' }, noLeadingTrailingSpaces] : [noLeadingTrailingSpaces]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Input placeholder="e.g., description" />
                                    </Form.Item>
                                  );
                                }}
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'middle' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'requireDescription']}
                                valuePropName="checked"
                                initialValue={true}
                                style={{ marginBottom: 0 }}
                              >
                                <Checkbox />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'needs_quantity']}
                                valuePropName="checked"
                                style={{ marginBottom: 0 }}
                              >
                                <Checkbox />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'unit']}
                                style={{ marginBottom: 0 }}
                                rules={[noLeadingTrailingSpaces]}
                              >
                                <Input placeholder="e.g., GB" />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              {fields.length > 1 && (
                                <MinusCircleOutlined
                                  onClick={() => remove(name)}
                                  style={{ color: '#ff4d4f', fontSize: 18, cursor: 'pointer' }}
                                />
                              )}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  <Button type="dashed" onClick={() => add({ requireDescription: true })} block icon={<PlusOutlined />}>
                    Add Entitlement
                  </Button>
                </>
              )}
            </Form.List>
          )}
        </>
      ) : (
        <>
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) =>
            prevValues?.existingProductId !== currentValues?.existingProductId ||
            prevValues?.isNewProduct !== currentValues?.isNewProduct
          }>
            {({ getFieldValue }) => {
              const existingProductId = getFieldValue('existingProductId');
              const currentIsNewProduct = getFieldValue('isNewProduct');

              // Check if product has both standard and org entitlements
              const hasStandardEntitlements = !currentIsNewProduct && existingProductId
                ? entitlements.some(ent => ent.product_id === existingProductId)
                : false;
              const hasOrgEntitlements = !currentIsNewProduct && existingProductId
                ? orgEntitlements.some(ent => ent.product_id === existingProductId)
                : false;

              // Hide checkbox if it's a new product OR if existing product has both types
              const shouldHideCheckbox = currentIsNewProduct || (hasStandardEntitlements && hasOrgEntitlements);

              return shouldHideCheckbox ? null : (
                <Form.Item name="useExistingOrgEntitlements" valuePropName="checked">
                  <Checkbox
                    onChange={(e) => setUseExistingEntitlements(e.target.checked)}
                  >
                    Use Existing Org Entitlements
                  </Checkbox>
                </Form.Item>
              );
            }}
          </Form.Item>

          {useExistingEntitlements ? (
            <Form.Item name="existingOrgEntitlementIds" label="Select Org Entitlements">
              <Select
                mode="multiple"
                placeholder="Select org entitlements"
                popupMatchSelectWidth={false}
                dropdownStyle={{ maxWidth: 600, whiteSpace: "normal", wordBreak: "break-word" }}
                tagRender={entitlementTagRender(orgEntitlements)}
                showSearch
                filterOption={(input, option) =>
                  (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {orgEntitlements.filter((ent) => ent.product_id === formData?.existingProductId).map((ent) => (
                  <Option key={ent.id} value={ent.id}>
                    {ent.name} - {ent.value} ({ent.access_type})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <Form.List name="orgEntitlements">
              {(fields, { add, remove }) => (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    <Button onClick={openEntitlementImport}>Import / paste CSV</Button>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                    <thead>
                      <tr style={{ ...getTableHeaderStyle() }}>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Product</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Name *</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Value *</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, width: 110 }} title="Require description before submitting. Uncheck to allow empty description (entitlement will not appear on SCC UI).">Req. Desc</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Access Type *</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Needs Quantity</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Unit</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Is Service</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, width: 60 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map(({ key, name, ...restField }) => (
                        <React.Fragment key={key}>
                          <Form.Item
                            noStyle
                            shouldUpdate={(prev, curr) =>
                              prev?.orgEntitlements?.[name]?.requireDescription !== curr?.orgEntitlements?.[name]?.requireDescription
                            }
                          >
                            {({ getFieldValue: getVal }) => {
                              const reqDesc = getVal(['orgEntitlements', name, 'requireDescription']);
                              const isRequired = reqDesc !== false;
                              return !isRequired ? (
                                <tr>
                                  <td colSpan={10} style={{ padding: '7px 8px 2px', borderBottom: 'none' }}>
                                    <Alert
                                      message="⚠️ No description — this entitlement will not appear on SCC UI"
                                      type="warning"
                                      style={{ fontSize: 12, padding: '4px 10px', borderRadius: 4, marginBottom: 2 }}
                                    />
                                  </td>
                                </tr>
                              ) : null;
                            }}
                          </Form.Item>
                          <tr style={{ ...getTableRowStyle() }}>
                            <td style={{ padding: '8px' }}>
                              <Form.Item noStyle shouldUpdate={(prev, curr) =>
                                prev?.orgEntitlements?.[name]?.productId !== curr?.orgEntitlements?.[name]?.productId ||
                                prev?.existingProductId !== curr?.existingProductId ||
                                prev?.newProductId !== curr?.newProductId ||
                                prev?.productName !== curr?.productName ||
                                prev?.isNewProduct !== curr?.isNewProduct
                              }>
                                {({ getFieldValue: getVal }) => {
                                  const productId = getVal(['orgEntitlements', name, 'productId']) ??
                                    (getVal('isNewProduct') ? getVal('newProductId') : getVal('existingProductId'));
                                  const product = products.find((item) => Number(item.id) === Number(productId));
                                  return <Text>{product?.name || (Number(productId) === Number(getVal('newProductId')) ? getVal('productName') : '') || productId || 'Current product'}</Text>;
                                }}
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'name']}
                                rules={[{ required: true, message: 'Required' }, noLeadingTrailingSpaces]}
                                style={{ marginBottom: 0 }}
                              >
                                <Input placeholder="e.g., feature_access" />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'value']}
                                rules={[{ required: true, message: 'Required' }, noLeadingTrailingSpaces]}
                                style={{ marginBottom: 0 }}
                              >
                                <Input placeholder="e.g., premium" />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <Form.Item
                                noStyle
                                shouldUpdate={(prev, curr) =>
                                  prev?.orgEntitlements?.[name]?.requireDescription !== curr?.orgEntitlements?.[name]?.requireDescription
                                }
                              >
                                {({ getFieldValue: getVal }) => {
                                  const reqDesc = getVal(['orgEntitlements', name, 'requireDescription']);
                                  const isRequired = reqDesc !== false;
                                  return (
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'description']}
                                      rules={isRequired ? [{ required: true, message: 'Description is required' }, noLeadingTrailingSpaces] : [noLeadingTrailingSpaces]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Input placeholder="e.g., description" />
                                    </Form.Item>
                                  );
                                }}
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'middle' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'requireDescription']}
                                valuePropName="checked"
                                initialValue={true}
                                style={{ marginBottom: 0 }}
                              >
                                <Checkbox />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'access_type']}
                                rules={[{ required: true, message: 'Required' }]}
                                style={{ marginBottom: 0 }}
                              >
                                <Select placeholder="Select">
                                  <Option value="R">(R) Read Only</Option>
                                  <Option value="RW">(RW) Read Write</Option>
                                </Select>
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'needs_quantity']}
                                valuePropName="checked"
                                style={{ marginBottom: 0 }}
                              >
                                <Checkbox />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'unit']}
                                style={{ marginBottom: 0 }}
                                rules={[noLeadingTrailingSpaces]}
                              >
                                <Input placeholder="e.g., users" />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'is_service']}
                                valuePropName="checked"
                                style={{ marginBottom: 0 }}
                              >
                                <Checkbox />
                              </Form.Item>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              {fields.length > 1 && (
                                <MinusCircleOutlined
                                  onClick={() => remove(name)}
                                  style={{ color: '#ff4d4f', fontSize: 18, cursor: 'pointer' }}
                                />
                              )}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  <Button type="dashed" onClick={() => add({ requireDescription: true })} block icon={<PlusOutlined />}>
                    Add Org Entitlement
                  </Button>
                </>
              )}
            </Form.List>
          )}
        </>
      )}

      <Divider />
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handlePrevious} size="large">
          Previous
        </Button>
        <Button type="primary" onClick={handleNext} size="large" icon={<ArrowRightOutlined />}>
          Next: Additional Settings
        </Button>
      </Space>
    </Card>
  );

  // Step 4: Additional Settings (Regions & Provisioning Questions)
  const renderAdditionalSettingsStep = () => {
    const handleRegionChange = (selectedRegions: string[]) => {
      // If GLOBAL is selected, clear all other selections and keep only GLOBAL
      if (selectedRegions.includes('GLOBAL') && selectedRegions.length > 1) {
        // If GLOBAL was just added, keep only GLOBAL
        const wasGlobalAlreadySelected = form.getFieldValue('allowedRegions')?.includes('GLOBAL');
        if (!wasGlobalAlreadySelected) {
          form.setFieldsValue({ allowedRegions: ['GLOBAL'] });
          message.warning('GLOBAL region selected. All other region selections have been cleared.');
        } else {
          // If GLOBAL was already selected, prevent adding other regions
          form.setFieldsValue({ allowedRegions: ['GLOBAL'] });
          message.warning('Cannot select other regions when GLOBAL is already selected.');
        }
      }
    };

    const selectedRegions = form.getFieldValue('allowedRegions') || [];
    const isGlobalSelected = selectedRegions.includes('GLOBAL');

    return (
      <Card title="Additional Settings">
        {/* Regions - Only for New Products */}
        {isNewProduct && (
          <>
            <Title level={4}>Allowed Regions</Title>

            {isGlobalSelected && (
              <Alert
                message="GLOBAL Region Selected"
                description="You have selected the GLOBAL region. No other region selection can be made. If you need to select specific regions, please remove GLOBAL first."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                closable
              />
            )}

            <Form.Item
              name="allowedRegions"
              label="Select Allowed Regions"
              rules={[{ required: true, message: 'Please select at least one region' }]}
            >
              <Select
                mode="multiple"
                placeholder="Select regions"
                showSearch
                onChange={handleRegionChange}
                filterOption={(input, option) =>
                  (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {regions.map((region) => (
                  <Option key={region.region_code} value={region.region_code}>
                    {region.region_name} ({region.region_code})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Divider />

            <Title level={4}>Provisioning Questions (Optional)</Title>
            <Form.Item name="hasProvisioningQuestions" valuePropName="checked">
              <Checkbox onChange={(e) => setHasProvisioningQuestions(e.target.checked)}>
              Add Provisioning Questions
              </Checkbox>
            </Form.Item>

            {hasProvisioningQuestions && (
              <Form.List name="provisioningQuestions">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Card
                        key={key}
                        size="small"
                        style={{ marginBottom: 8 }}
                        title={`Question ${name + 1}`}
                        extra={<MinusCircleOutlined onClick={() => remove(name)} />}
                      >
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          label="Question Name (Internal)"
                          rules={[{ required: true, message: 'Required' }, noLeadingTrailingSpaces]}
                        >
                          <Input placeholder="e.g., tenant_region" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'display_name']}
                          label="Display Name"
                          rules={[{ required: true, message: 'Required' }, noLeadingTrailingSpaces]}
                        >
                          <Input placeholder="e.g., Select Tenant Region" />
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'description']} label="Description">
                          <TextArea rows={2} placeholder="Question description" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'type']}
                          label="Question Type"
                          rules={[{ required: true, message: 'Required' }]}
                        >
                          <Select placeholder="Select type">
                            <Option value="text">Text</Option>
                            <Option value="select">Select</Option>
                            <Option value="multiselect">Multi-Select</Option>
                            <Option value="boolean">Boolean</Option>
                          </Select>
                        </Form.Item>
                      </Card>
                    ))}
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Provisioning Question
                    </Button>
                  </>
                )}
              </Form.List>
            )}
          </>
        )}

        {!isNewProduct && (
          <Alert
            message="Additional settings are only required for new products"
            description="Since you're using an existing product, regions and provisioning questions are already configured."
            type="info"
            showIcon
          />
        )}

        <Divider />
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={handlePrevious} size="large">
          Previous
          </Button>
          <Button type="primary" onClick={handleNext} size="large" icon={<ArrowRightOutlined />}>
          Next: Review & Submit
          </Button>
        </Space>
      </Card>
    );
  };

  return (
    <div className="product-onboarding-container">
      <Title level={2} style={{ marginBottom: 8 }}>Product/SKU Onboarding Wizard</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Complete the step-by-step wizard to onboard products, SKUs, or both together.
      </Text>

      {currentStep > 0 && (
        <Alert
          message={`Current Flow: ${
            onboardingType === 'product' ? 'Product Only' :
              onboardingType === 'sku' ? 'SKU Only' :
                'Product + SKU Together'
          }`}
          type="info"
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Steps
        current={currentStep}
        style={{ marginBottom: 32 }}
        items={
          currentStep === 0 ? [
            { title: 'Select Type', description: 'Choose onboarding flow' },
            { title: 'Configure', description: 'Fill details' },
            { title: 'Review', description: 'Review & submit' },
          ] :
            onboardingType === 'product' ? [
              { title: 'Type', description: 'Onboarding type' },
              { title: 'Product', description: 'Product details' },
              { title: 'Entitlements', description: 'Configure entitlements' },
              { title: 'Settings', description: 'Regions & questions' },
              { title: 'Review', description: 'Review & submit' },
            ] :
              onboardingType === 'sku' ? [
                { title: 'Type', description: 'Onboarding type' },
                { title: 'SKU', description: 'SKU configuration' },
                { title: 'Products', description: 'Link to products' },
                { title: 'Review', description: 'Review & submit' },
              ] :
                onboardingType === 'migrate' ? [
                  { title: 'Type',        description: 'Onboarding type'      },
                  { title: 'Select SKUs', description: 'Filter & pick SKUs'   },
                  { title: 'Map Names',   description: 'Set target SKU names' },
                  { title: 'Review',      description: 'Review & confirm'     },
                ] :
                  [
                    { title: 'Type', description: 'Onboarding type' },
                    { title: 'Product', description: 'Product information' },
                    { title: 'SKU', description: 'SKU configuration' },
                    { title: 'Entitlements', description: 'Configure entitlements' },
                    { title: 'Settings', description: 'Regions & questions' },
                    { title: 'Review', description: 'Review & submit' },
                  ]
        }
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          onboardingType: 'both',
          isNewProduct: true,
          provisioningClient: 'CLIENT_COMMON',
          provisioningStyle: 'STYLE_ASYNC',
          isNewSKU: true,
          isOrgEntitled: false,
          isAddonSKU: false,
          skuType: 'single',
          isExternalSKU: false,
          buyingModel: undefined,
          useExistingEntitlements: false,
          hasProvisioningQuestions: false,
          is_hidden: false,
          is_auto_provision: false,
          is_composite: false,
          is_brownfield_only: false,
          is_unified_onboarding_supported: false,
          is_parent_provisioning_supported: false,
          inherit_parent_metadata: false,
          entitlements: [{ name: '', value: '', needs_quantity: false, requireDescription: true }],
          orgEntitlements: [{ name: '', value: '', needs_quantity: false, access_type: 'RW', is_service: false, requireDescription: true }],
          provisioningQuestions: [],
          allowedRegions: [],
          productConfigs: [],
          skus: [{
            skuName: '',
            isExternalSKU: false,
            isDevTest: false,
            buyingModel: undefined,
            skuType: 'single',
            productConfigs: [{}]
          }],
          skuOrgEntitlementConfigs: [],
        }}
      >
        {currentStep === 0 && renderOnboardingTypeStep()}

        {/* Product Only Flow */}
        {onboardingType === 'product' && currentStep === 1 && renderProductStep()}
        {onboardingType === 'product' && currentStep === 2 && renderEntitlementsStep()}
        {onboardingType === 'product' && currentStep === 3 && renderAdditionalSettingsStep()}
        {onboardingType === 'product' && currentStep === 4 && renderPreview()}

        {/* SKU Only Flow */}
        {onboardingType === 'sku' && currentStep === 1 && renderSKUStep()}
        {onboardingType === 'sku' && currentStep === 2 && renderPreview()}

        {/* Product + SKU Flow */}
        {onboardingType === 'both' && currentStep === 1 && renderProductStep()}
        {onboardingType === 'both' && currentStep === 2 && renderSKUStep()}
        {onboardingType === 'both' && currentStep === 3 && renderEntitlementsStep()}
        {onboardingType === 'both' && currentStep === 4 && renderAdditionalSettingsStep()}
        {onboardingType === 'both' && currentStep === 5 && renderPreview()}

        {/* Migrate SKUs Flow — outer steps 1-3 are owned by SkuMigration */}
        {onboardingType === 'migrate' && currentStep >= 1 && (
          <SkuMigration
            outerStep={currentStep}
            onOuterNext={() => setCurrentStep((s) => s + 1)}
            onOuterPrev={() => setCurrentStep((s) => s - 1)}
            onBackToType={() => setCurrentStep(0)}
          />
        )}
      </Form>
    </div>
  );
};

export default ProductOnboarding;
